import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Language as LanguageIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Preview as PreviewIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import SharedSidebar from '../../../components/layout/SharedSidebar';
import UserBrowserSidebar from '../../../components/shared/UserBrowserSidebar';
import ScrapedContentModal from './ScrapedContentModal';
import { ProposalHistorySelector, ProposalHistorySelectorHandle } from './ProposalHistorySelector';
import PromptSettingsModal from './PromptSettingsModal';
import { User, ClientContext, FormatType } from '../types';
import { ProposalService } from '../services/proposalService';
import { useAuth } from '../../../contexts/AuthContext';

interface ProposalSidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  selectedUsers: User[];
  onUsersChange: (users: User[]) => void;
  clientContext: ClientContext;
  onClientChange: (context: ClientContext) => void;
  formatType: FormatType;
  onFormatChange: (format: FormatType) => void;
  onGenerate: () => void;
  isGenerating: boolean;
  service?: ProposalService | null;
  onLoadProposal?: (proposalId: string, clientName: string) => void;
  onReset?: () => void;
  currentProposalId?: string;
  currentClientName?: string;
  refreshTrigger?: number;
  resetCounter?: number;
  historyRef?: React.RefObject<ProposalHistorySelectorHandle>;
}

const ProposalSidebar: React.FC<ProposalSidebarProps> = ({
  isOpen,
  onToggle,
  selectedUsers,
  onUsersChange,
  clientContext,
  onClientChange,
  formatType,
  onFormatChange,
  onGenerate,
  isGenerating,
  service,
  onLoadProposal,
  onReset,
  currentProposalId,
  currentClientName,
  refreshTrigger,
  resetCounter,
  historyRef
}) => {
  const [crawling, setCrawling] = useState(false);
  const [crawlError, setCrawlError] = useState<string | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showPromptSettings, setShowPromptSettings] = useState(false);

  const { hasAnyGroup } = useAuth();

  // Check if user is admin.
  // AUTHCFG-REVIEW-003 (Round 7 sweep #134, finding 955ff628): match
  // the fail-closed pattern in features.ts / AdminPermissionSwitch.tsx
  // / ProjectTracker.tsx. Empty env var → no group → no admin UI.
  const adminGroupId = process.env.REACT_APP_PROFILE_ADMIN_GROUP_ID || '';
  const isAdmin = hasAnyGroup(adminGroupId ? [adminGroupId] : []);

  // Check if we can generate - only require team members, client context is optional
  const canGenerate = selectedUsers.length > 0 &&
    !isGenerating &&
    !crawling;

  // Handle URL crawl
  const handleCrawl = useCallback(async () => {
    if (!clientContext.url || !service) return;
    
    setCrawling(true);
    setCrawlError(null);
    
    // Clear existing extracted info and scrapedContent immediately when starting a new crawl
    onClientChange({
      ...clientContext,
      scrapedContent: undefined,
      extractedInfo: undefined
    });
    
    try {
      const result = await service.scrapeWebsite(clientContext.url);
      
      // Log the response to debug
      console.log('Scrape result:', result);
      
      // Update context with extracted info
      // Handle company_info from backend (with snake_case fields)
      let extractedCompanyName = 'Unknown';
      let extractedTherapeuticArea = 'Unknown';
      let extractedModality = 'Unknown';
      
      if (result.company_info) {
        extractedCompanyName = result.company_info.company_name || 'Unknown';
        extractedTherapeuticArea = result.company_info.therapeutic_area || 'Unknown';
        extractedModality = result.company_info.modality || 'Unknown';
      } else if (result.extractedInfo) {
        extractedCompanyName = result.extractedInfo.companyName || 'Unknown';
        extractedTherapeuticArea = result.extractedInfo.therapeuticArea || 'Unknown';
        extractedModality = result.extractedInfo.modality || 'Unknown';
      }
      
      console.log('Company info extracted:', {
        companyName: extractedCompanyName,
        therapeuticArea: extractedTherapeuticArea,
        modality: extractedModality
      });
      
      onClientChange({
        ...clientContext,
        scrapedContent: result.content,
        extractedInfo: {
          companyName: extractedCompanyName,
          therapeuticArea: extractedTherapeuticArea,
          modality: extractedModality,
          crawledAt: new Date()
        }
      });
    } catch (error) {
      setCrawlError('Failed to scrape website. Please try again or use manual input.');
      console.error('Crawl error:', error);
      // Clear any partial data on error
      onClientChange({
        ...clientContext,
        scrapedContent: undefined,
        extractedInfo: undefined
      });
    } finally {
      setCrawling(false);
    }
  }, [clientContext, onClientChange, service]);

  // Calculate cache status
  const getCacheStatus = () => {
    if (!clientContext.extractedInfo?.crawledAt) return null;
    
    const crawledAt = new Date(clientContext.extractedInfo.crawledAt);
    const now = new Date();
    const hoursElapsed = (now.getTime() - crawledAt.getTime()) / (1000 * 60 * 60);
    
    if (hoursElapsed > 24) {
      return { status: 'expired', color: 'error' as const, icon: <ErrorIcon /> };
    } else if (hoursElapsed > 22) {
      return { status: 'expiring', color: 'warning' as const, icon: <WarningIcon /> };
    } else {
      return { status: 'valid', color: 'success' as const, icon: <CheckCircleIcon /> };
    }
  };

  const cacheStatus = getCacheStatus();

  return (
    <>
    <SharedSidebar
      isOpen={isOpen}
      onToggle={onToggle}
      title={
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <span>Bio Generator</span>
          {isAdmin && (
            <IconButton
              size="small"
              onClick={() => setShowPromptSettings(true)}
              sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'rgba(255,255,255,0.9)' } }}
              title="Prompt & Template Settings"
            >
              <SettingsIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      }
      subtitle="Configure and generate tailored bios"
    >
      <Box sx={{ p: 2 }}>
        {/* Proposal Selector */}
        {(onLoadProposal || onReset) && (
          <Box sx={{ mb: 3 }}>
            <ProposalHistorySelector
              ref={historyRef}
              onLoad={onLoadProposal!}
              onReset={onReset}
              currentProposalId={currentProposalId}
              currentClientName={currentClientName}
              refreshTrigger={refreshTrigger}
            />
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Team Selection */}
        <Box sx={{ mb: 3 }}>
          <UserBrowserSidebar
            key={`user-browser-${resetCounter || 0}`}
            filters={{
              hasProfile: {
                value: true,
                visible: false,
                locked: true
              }
            }}
            multiSelect={true}
            maxSelections={20}
            onSelectionChange={onUsersChange}
            initialSelection={selectedUsers}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Client Context */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom color="text.primary">
            Client Information
          </Typography>
          
          {/* URL Input */}
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Company Website URL"
              value={clientContext.url || ''}
              onChange={(e) => onClientChange({ ...clientContext, url: e.target.value })}
              placeholder="https://example-lifesciences.com"
              InputProps={{
                startAdornment: <LanguageIcon sx={{ mr: 1, color: 'action.active' }} />
              }}
              sx={{ 
                mb: 1,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(79, 172, 254, 0.02)',
                  '&:hover': {
                    backgroundColor: 'rgba(79, 172, 254, 0.05)',
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'white',
                  }
                }
              }}
            />
            <Button
              variant="outlined"
              onClick={handleCrawl}
              disabled={!clientContext.url || crawling}
              startIcon={crawling ? <CircularProgress size={16} /> : <RefreshIcon />}
              fullWidth
            >
              {crawling ? 'Scraping...' : 'Scrape Website'}
            </Button>
          </Box>

          {/* Cache Status */}
          {cacheStatus && clientContext.extractedInfo && (
            <Alert 
              severity={cacheStatus.color}
              icon={cacheStatus.icon}
              sx={{ mb: 2 }}
              action={
                cacheStatus.status === 'expired' && (
                  <Button
                    size="small"
                    onClick={handleCrawl}
                    disabled={crawling}
                  >
                    Re-scrape
                  </Button>
                )
              }
            >
              {cacheStatus.status === 'valid' && 'Website data cached and valid'}
              {cacheStatus.status === 'expiring' && 'Cache expiring soon (< 2 hours)'}
              {cacheStatus.status === 'expired' && 'Cache expired - please re-scrape'}
            </Alert>
          )}

          {/* Extracted Info Display */}
          {clientContext.extractedInfo && (
            <Box sx={{ 
              mb: 2, 
              p: 2, 
              bgcolor: 'rgba(79, 172, 254, 0.02)', 
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              position: 'relative'
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Extracted Company Info:
                  </Typography>
                  <Typography variant="body2" color="text.primary">
                    <strong>Company:</strong> {clientContext.extractedInfo.companyName}
                  </Typography>
                  <Typography variant="body2" color="text.primary">
                    <strong>Therapeutic Area:</strong> {clientContext.extractedInfo.therapeuticArea}
                  </Typography>
                  <Typography variant="body2" color="text.primary">
                    <strong>Modality:</strong> {clientContext.extractedInfo.modality}
                  </Typography>
                </Box>
                <Tooltip title="View scraped content">
                  <IconButton
                    size="small"
                    onClick={() => setShowPreviewModal(true)}
                    sx={{ 
                      ml: 1,
                      bgcolor: 'rgba(79, 172, 254, 0.1)',
                      '&:hover': {
                        bgcolor: 'rgba(79, 172, 254, 0.2)'
                      }
                    }}
                  >
                    <PreviewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}

          {/* Manual Input */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Project Overview</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Additional Context"
                value={clientContext.manualInput || ''}
                onChange={(e) => onClientChange({ ...clientContext, manualInput: e.target.value })}
                placeholder="Describe the scope of the project assignment and any additional information about the client that might be useful to tailor the bios..."
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(79, 172, 254, 0.02)',
                    '&:hover': {
                      backgroundColor: 'rgba(79, 172, 254, 0.05)',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'white',
                    }
                  }
                }}
              />
            </AccordionDetails>
          </Accordion>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Bio Format Selection */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom color="text.primary">
            Bio Format
          </Typography>
          <RadioGroup
            value={formatType}
            onChange={(e) => onFormatChange(e.target.value as FormatType)}
            sx={{
              '& .MuiFormControlLabel-root': {
                padding: '6px 12px',
                margin: '2px 0',
                borderRadius: '8px',
                border: '1px solid transparent',
                '&:hover': {
                  backgroundColor: 'rgba(79, 172, 254, 0.02)',
                  borderColor: 'divider'
                }
              },
              '& .Mui-checked + .MuiFormControlLabel-label': {
                fontWeight: 600
              }
            }}
          >
            <FormControlLabel
              value="full"
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography variant="body2" color="text.primary" fontWeight={600}>Full Bio (PDF)</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Complete professional biography
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="one-slider"
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography variant="body2" color="text.primary" fontWeight={600}>1 Bio per Slide (PPTX)</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Individual detailed slides
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="intro"
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography variant="body2" color="text.primary" fontWeight={600}>4 Bios per Slide (PPTX)</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Condensed team overview
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="web-bio"
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography variant="body2" color="text.primary" fontWeight={600}>Web Bio (PDF)</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Professional website biography
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Generate Button */}
        <Button
          variant="contained"
          onClick={onGenerate}
          disabled={!canGenerate}
          fullWidth
          size="large"
          startIcon={isGenerating ? <CircularProgress size={20} /> : null}
          sx={{
            background: 'linear-gradient(135deg, #4FACFE 0%, #00B4DB 100%)',
            color: 'white',
            fontWeight: 600,
            py: 1.5,
            '&:hover': {
              background: 'linear-gradient(135deg, #4FACFE 0%, #00B4DB 100%)',
              opacity: 0.9,
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(79, 172, 254, 0.3)'
            },
            '&:disabled': {
              background: 'rgba(0, 0, 0, 0.12)',
              color: 'rgba(0, 0, 0, 0.26)'
            }
          }}
        >
          {isGenerating ? 'Generating Bios...' : 'Generate Bios'}
        </Button>

        {/* Error Display */}
        {crawlError && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setCrawlError(null)}>
            {crawlError}
          </Alert>
        )}
      </Box>

      {/* Scraped Content Preview Modal */}
      <ScrapedContentModal
        open={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        url={clientContext.url}
        scrapedContent={clientContext.scrapedContent}
        extractedInfo={clientContext.extractedInfo}
      />
    </SharedSidebar>

    {/* Prompt Settings Modal */}
    <PromptSettingsModal
      open={showPromptSettings}
      onClose={() => setShowPromptSettings(false)}
    />
    </>
  );
};

export default ProposalSidebar;
